import { type MenuItemModel, type MenuModel, type MenuSectionModel } from '../../ui/menuModel';
import {
  audioOptions,
  decideAudioTrack,
  chapterIndexAt as chapterIndexOf,
  channelRouteOf,
  clampPlaybackRate,
  decideVideoTrack,
  formatRoute,
  instanceRouteUrl,
  jumpOutLinks,
  peerTubeWatchUrl,
  PLAYBACK_RATES,
  pickImage,
  playlistEntryAuthorMissing,
  playlistEntryUnavailable,
  positionFraction,
  qualityOptions,
  chapterSponsorBadges,
  richTextToPlain,
  scrubRange,
  seekSecondsAt,
  sponsorMarkAt,
  sponsorMarksFor,
  storyboardCueAt,
  storyboardPreview,
  subtitleOptions,
  timelineSegments,
  youtubeRouteUrl,
  type AudioTrackInfo,
  type Chapter,
  type CodecFamily,
  type ImageRef,
  type InstanceConfig,
  type MediaSource_,
  type PeerTubeSearchResult,
  type PlaybackPlan,
  type PlaylistEntry,
  type PlaybackPreferences,
  type QueueLinkedRow,
  type ResolvedSponsorSegment,
  type RichNode,
  type Route,
  type ScrubRange,
  type SkipRun,
  type SponsorBarMark,
  type StoryboardCue,
  type StoryboardPreview,
  type SubtitleTrackRef,
  type TimelineSegment,
  type VideoDetails,
  type VideoSummary,
  type VideoTrackInfo,
} from '@visious/core';
import { toRichNodeViews, type RichNodeView } from '../../richText/model';
import type { ApiProblem } from '../../api/videos';
import { describeProblem } from '../../api/problem';
import {
  toListingItems,
  type ListingBadge,
  type ListingItem,
  type ListingMarker,
  type ListingProgress,
  type ProgressLookup,
} from '../../listing/model';
import { formatDuration } from '../../format';
import { fmt, text } from '../../strings';

/**
 * The Watch screen's reasoning (R13). Everything here is a pure function of plain data: no DOM, no
 * refs, no React. `useWatchScreen.ts` is the React binding that feeds it, `view.tsx` renders what
 * it returns and decides nothing.
 *
 * The split is stricter than R13's literal sentence rather than looser. R13 says the `model.ts` *is*
 * the `useXxxScreen()` hook, but a page that mounts a `<video>` needs a `RefObject<HTMLVideoElement>`
 * — a DOM type — and putting that in `model.ts` would put a DOM type in the one file whose whole
 * job is not having any. So the hook lives next door and this file stays portable, which is
 * stricter than the rule rather than looser.
 *
 * Formatting happens here, not in core (R7): core hands over `viewCount: number` and the page turns
 * it into `1.2M`. That is why `format/` is an app module.
 */

// Re-exported so `view.tsx` can name what it renders without importing core (non-negotiable 2 —
// a type-only import is still an import, and it is the edge that keeps the Android path open).
export type {
  Chapter,
  MediaSource_,
  ResolvedSponsorSegment,
  RichNode,
  ScrubRange,
  SponsorBarMark,
  StoryboardPreview,
  TimelineSegment,
};

/**
 * The rail renders the shared listing row (`listing/model.ts`). Re-exported under the name the rail's
 * own files use, so `Recommendations.tsx` still imports its view model from the screen it belongs to
 * rather than reaching across the app for it — the same edge R13 draws everywhere else.
 */
export type { ListingItem };

// ---------------------------------------------------------------------------
// What the player area shows — one arm per `PlaybackPlan` arm, plus the two
// states a plan cannot describe (still loading, request failed).
// ---------------------------------------------------------------------------

export type WatchStage =
  | { readonly kind: 'loading' }
  /*
   * `retryable` is the problem taxonomy's own grade (`api/problem.ts`), carried this far instead of
   * being dropped: a video YouTube took down, a region lock and a wrong instance address all answer
   * a second identical request identically, and a Try again in front of one is a button that spends
   * a reader's attention to reproduce the same sentence. The way out of those is the jump-out menu
   * `StageFailure` draws beside it, which works with no video and even with no instance. A network
   * failure or a sick instance keeps the button.
   *
   * The Shaka load-error overlay is a different surface and keeps its retry unconditionally — there
   * the button is also how "the error stays until a load succeeds" gets its escape hatch.
   */
  | {
      readonly kind: 'problem';
      readonly title: string;
      readonly detail: string;
      readonly retryable: boolean;
    }
  /** Adaptive playback: Shaka, headless, fed from `source`. */
  | {
      readonly kind: 'player';
      readonly source: MediaSource_;
      readonly startSec: number | null;
      readonly live: boolean;
      readonly durationSec: number | null;
    }
  /** §7's no-MSE row: itag 18 through a plain `src`, with the limitation stated rather than hidden. */
  | { readonly kind: 'progressive'; readonly url: string; readonly notice: string }
  | { readonly kind: 'upcoming'; readonly premiereText: string | null }
  /**
   * Never retryable, so it carries no flag to say so: every reason in {@link unplayableCopy} is a
   * fact about the video or the browser rather than about the request that read it.
   */
  | { readonly kind: 'unplayable'; readonly title: string; readonly detail: string };

/**
 * §7's list, in the app's words. `no-source` is the one that needs explaining rather than
 * apologising for: it is the captured state where a live/DVR video has no `hlsUrl` *and* a broken
 * `dashUrl`, so there is genuinely nothing to play — not a bug on our side, and not "try again".
 */
function unplayableCopy(): Record<string, { title: string; detail: string } | undefined> {
  const words = text().watch.unplayable;
  return {
    region: words.region,
    paid: words.paid,
    private: words.private,
    removed: words.removed,
    'no-mse': words.noMse,
    'no-source': words.noSource,
  };
}

export interface StageOptions {
  /** From `parseRoute`: `t=`/`start=`, already folded into seconds. */
  readonly startSec: number | null;
  /** `shaka.Player.isBrowserSupported()`. Decides the §7 no-MSE branch. */
  readonly mseSupported: boolean;
  readonly nowEpochSec: number;
}

export function stageForPlan(plan: PlaybackPlan, options: StageOptions): WatchStage {
  switch (plan.kind) {
    case 'upcoming':
      return {
        kind: 'upcoming',
        premiereText: fmt().published(plan.premiereAtEpochSec, options.nowEpochSec),
      };

    case 'unplayable': {
      const copy = unplayableCopy()[plan.reason] ?? {
        title: text().watch.unplayable.unknownTitle,
        detail: text().watch.unplayable.unknownDetail(plan.reason),
      };
      return { kind: 'unplayable', ...copy };
    }

    case 'live':
    case 'dvr':
    case 'vod': {
      // `toPlaybackPlan` only returns `no-mse` when there is *nothing* to fall back to; a browser
      // without MSE that does have itag 18 still gets a `vod` plan, and the choice of path is the
      // platform's. Doing it here rather than in core is deliberate — "can this engine play a DASH
      // manifest" is not a fact about the video.
      if (!options.mseSupported) {
        const url = plan.source.fallbackProgressiveUrl;
        if (url === null) return { kind: 'unplayable', ...text().watch.unplayable.noMse };
        return { kind: 'progressive', url, notice: text().watch.progressiveNotice };
      }

      const live = plan.kind === 'live';
      return {
        kind: 'player',
        source: plan.source,
        // §7/0f — on live there is no stable position to resume to: the seekable window has moved
        // by the time anything lands, so the live edge is the only time that still means the same.
        startSec: live ? null : options.startSec,
        live,
        durationSec: plan.kind === 'vod' || plan.kind === 'dvr' ? plan.durationSec : null,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Request failure → something a reader can act on (R6's kinds, in prose)
// ---------------------------------------------------------------------------

/** §3's instance resolution failing is a *configuration* problem, not a request problem. */
export function stageForBootstrapProblem(problem: { readonly kind: string; readonly url?: string }): WatchStage {
  const words = text().watch.bootstrap;
  // Neither is retryable: both sentences send the reader to Settings, and nothing about the address
  // changes between two attempts made from this page.
  if (problem.kind === 'bad-instance') {
    return {
      kind: 'problem',
      title: words.badInstanceTitle,
      detail: words.badInstanceDetail(problem.url ?? words.badInstanceFallback),
      retryable: false,
    };
  }
  return {
    kind: 'problem',
    title: words.noInstanceTitle,
    detail: words.noInstanceDetail,
    retryable: false,
  };
}

/**
 * R6's kinds in prose. The taxonomy moved to `api/problem.ts` — the feed pages are its
 * second and third callers — and what stays here is the two sentences that are genuinely about a
 * *video*: a 404 means this video is gone, not that the endpoint is missing, and an unreadable body
 * is an unreadable video.
 */
export function stageForProblem(problem: ApiProblem): WatchStage {
  const words = text().watch.problem;
  const copy = describeProblem(problem, {
    notFound: words.notFound,
    // The four the endpoint names outright: this page is where "removed", "private", "age-restricted"
    // and "members-only" arrive, since it is the one that fetches a video by id (`videoGoneSubject`).
    removed: words.removed,
    private: words.private,
    ageRestricted: words.ageRestricted,
    membersOnly: words.membersOnly,
    /*
     * The fifth, and the one this page turns from seconds into words. `published` reads nothing but
     * the *difference* between its two arguments, so a wait already measured from now is passed as
     * `(startsInSec, 0)` — no clock to ask, and the same relative phrasing every other time on this
     * page wears ("in 3 hours").
     */
    notYetLive: (startsInSec) =>
      words.notYetLive(startsInSec === null ? null : fmt().published(startsInSec, 0)),
    unreadable: () => words.unreadable,
  });

  return { kind: 'problem', title: copy.title, detail: copy.detail, retryable: copy.retryable };
}

/**
 * What kind of statement the corner is making, which is a **model** answer and not a spelling of one
 * (2026-08-08).
 *
 * Below the `md` step the corner player is a bar with a 112 px tile, and a sentence drawn on a
 * thumbnail is a few words squeezed into a picture. There the box draws a glyph instead — but which
 * glyph is a question about what the sentence *says*, and the view has nothing to read that off a
 * finished string with. So the sentence travels with its kind, the model picks the kind, and the
 * view picks the picture (`MiniNoticeLine.tsx`) and keeps the sentence for the reader who is
 * listening rather than looking.
 *
 * Four kinds, and they are the distinctions the corner already draws rather than a taxonomy invented
 * for the glyphs:
 *
 * - `refusal` — the video will not play, whether the instance said so ({@link WatchStage}'s `problem`
 *   and `unplayable`) or the load itself failed. All three are the same fact to a reader in a corner:
 *   this one is not going to start, and what to do about it is on the video's own page.
 * - `premiere` — a date rather than a defect.
 * - `page-only` — there is a video, and only the full page can play it.
 * - `ended` — the queue has played its list out. Not a stage at all: it is what the corner says once
 *   there is no player left in it (`playback/QueueEnded.tsx`).
 */
export type MiniNoticeKind = 'refusal' | 'premiere' | 'page-only' | 'ended';

/** A sentence the corner player puts up, and what kind of statement it is. */
export interface MiniNotice {
  readonly text: string;
  readonly kind: MiniNoticeKind;
}

/**
 * The one line the **minimized** player puts up in place of a picture it has none of, or `null`
 * where there is one to draw.
 *
 * A corner player that renders nothing is a box of zero height, which is a queue that has silently
 * stopped: no reason given, and no transport to step past whatever went wrong. So every stage that
 * cannot produce a picture answers with a sentence, and the stages that can — `player`, and
 * `loading`, whose video is on its way rather than in trouble — answer with `null`.
 *
 * The refusals speak with **their own title** rather than through one shared "cannot play this"
 * (`strings/…/watch.ts` says why that list is worth keeping apart): "Private video" and "Not
 * available in this region" cost the same line and are different facts to act on. The detail under
 * each of them, Try again and the Watch on menu are all a Maximize away, which is the same split a
 * failed *load* already makes while minimized.
 */
export function miniStageNotice(stage: WatchStage): MiniNotice | null {
  switch (stage.kind) {
    case 'player':
    case 'loading':
      return null;
    case 'problem':
    case 'unplayable':
      return { text: stage.title, kind: 'refusal' };
    case 'upcoming': {
      // A date rather than a defect, so it says the date — the page's own two sentences, one for a
      // premiere whose time is known and one for a premiere whose time is not.
      const words = text().watch.page;
      const when =
        stage.premiereText === null ? words.premiereUnknown : words.premiereAt(stage.premiereText);
      return { text: when, kind: 'premiere' };
    }
    case 'progressive':
      return { text: text().watch.mini.pageOnly, kind: 'page-only' };
  }
}

/**
 * A load that failed under a minimized player, in the same two fields.
 *
 * The kind is decided here rather than at the call site because it is the one judgement in it: a
 * `load()` that threw is a **refusal** like the stage's own, however differently it arrived. The
 * sentence says where the detail is instead of trying to fit it, since every way out of a failure
 * — Try again, the Watch on menu — is full-size.
 */
export function miniPlayerErrorNotice(): MiniNotice {
  return { text: text().watch.mini.failed, kind: 'refusal' };
}

// ---------------------------------------------------------------------------
// The page's text and links
// ---------------------------------------------------------------------------

export interface WatchChapter {
  readonly title: string;
  readonly startSec: number;
  readonly endSec: number;
  readonly startText: string;
}

export interface WatchVideo {
  readonly videoId: string;
  readonly title: string;
  readonly author: string;
  /** For the subscribe control. `null` on a collaboration video, whose `authorId` is `""` (§2). */
  readonly channelId: string | null;
  readonly authorHref: string | null;
  readonly authorVerified: boolean;
  readonly authorAvatarUrl: string | null;
  readonly viewCountText: string | null;
  readonly viewCountTitle: string | null;
  readonly likeCountText: string | null;
  readonly likeCountTitle: string | null;
  readonly publishedText: string | null;
  readonly publishedTitle: string | null;
  readonly descriptionNodes: readonly RichNodeView[];
  /** An empty description renders nothing at all — no frame, no Show-more for a blank. */
  readonly hasDescription: boolean;
  readonly chapters: readonly WatchChapter[];
  /**
   * The picture and the length, which this page itself never draws — the player is the picture, and
   * the bar is the length. They are here for the **queue** (2026-08-07): building a queue around the
   * video already playing needs a row's worth of it, and the watch payload is the only place the app
   * is sure what this video is called, who made it and how long it runs. Deriving a thumbnail URL by
   * hand instead would be a second, unverified opinion about an address the payload already carries.
   */
  readonly thumbnailUrl: string | null;
  /** `null` for live and for a premiere, where `lengthSeconds` is `0` and means "not a length". */
  readonly durationSec: number | null;
}

/**
 * The byline's second line — the channel page's own figure, worded by the same catalogue
 * entry shape. `null` while the R14 cache has nothing for this channel, which renders as absence.
 */
export function subscriberLine(count: number | null): string | null {
  const formatted = fmt().count(count);
  return formatted === null ? null : text().watch.page.subscribers(formatted);
}

/**
 * The channel a byline links to — a video's, a recommendation's or a comment's, since all three
 * carry the same pair of fields.
 *
 * `channelRouteOf` rather than the hand-rolled version this replaced: a collaboration video has
 * `authorId: ""` and `authorUrl: "/channel/"`, and the old "non-empty means usable" test let that
 * through as a link to "Nothing here" (see `links/routes.ts`).
 */
export function channelHrefOf(target: {
  readonly authorId: string | null;
  readonly authorUrl: string | null;
}): string | null {
  const route = channelRouteOf(target);
  return route === null ? null : formatRoute(route);
}

/** The smallest avatar that is still ≥ 48 px, so a 512 px source is not scaled down on every page. */
function avatarUrlOf(images: readonly ImageRef[]): string | null {
  return pickImage(images, { minWidth: 48 })?.url ?? null;
}

export function toWatchVideo(
  video: VideoDetails,
  chapters: readonly Chapter[],
  nowEpochSec: number,
): WatchVideo {
  return {
    videoId: video.videoId,
    title: video.title,
    author: video.author,
    channelId: video.authorId !== null && video.authorId.trim() !== '' ? video.authorId : null,
    authorHref: channelHrefOf(video),
    authorVerified: video.authorVerified,
    authorAvatarUrl: avatarUrlOf(video.authorThumbnails),
    thumbnailUrl:
      pickImage(video.thumbnails, { minWidth: RAIL_THUMBNAIL_MIN_WIDTH, widescreen: true })?.url ??
      null,
    durationSec: video.lengthSeconds > 0 ? video.lengthSeconds : null,
    viewCountText: fmt().count(video.viewCount),
    // `null` where the exact figure would only restate the label — German does not compact below a
    // million, so both were "20.955" on the video this was found on (`distinctExactTitle`).
    viewCountTitle: fmt().distinctExactTitle(fmt().count(video.viewCount), fmt().exactCount(video.viewCount)),
    likeCountText: fmt().count(video.likeCount),
    likeCountTitle: fmt().distinctExactTitle(fmt().count(video.likeCount), fmt().exactCount(video.likeCount)),
    publishedText: fmt().published(video.publishedEpochSec, nowEpochSec),
    publishedTitle: fmt().absoluteDate(video.publishedEpochSec),
    // No duration here on purpose: the player itself states it, and the page saying it twice was
    // "remove video length from the player page".
    descriptionNodes: toRichNodeViews(video.descriptionNodes),
    // Via the plain text rather than `length > 0`: a description of pure whitespace still parses
    // into a text node, and *nothing readable* means nothing drawn.
    hasDescription: richTextToPlain(video.descriptionNodes).trim() !== '',
    chapters: chapters.map((chapter) => ({ ...chapter, startText: formatDuration(chapter.startSec) })),
  };
}

/**
 * Return YouTube Dislike's counter, right of the like one (`integrations.md`) — the like count's
 * own formatting, over a figure that arrives from `useReturnYoutubeDislike` instead of the video
 * payload. `null` while the integration is off, the lookup has not answered, or it answered
 * nothing for this video.
 */
export function dislikeCounterView(dislikeCount: number | null): {
  readonly text: string | null;
  readonly title: string | null;
} {
  return {
    text: fmt().count(dislikeCount),
    title: fmt().distinctExactTitle(fmt().count(dislikeCount), fmt().exactCount(dislikeCount)),
  };
}

/** The chapters a plan carries. Only a VOD plan has them — live has no fixed timeline to cut up. */
export function chaptersOf(plan: PlaybackPlan | null): readonly Chapter[] {
  return plan !== null && plan.kind === 'vod' ? plan.chapters : [];
}

// ---------------------------------------------------------------------------
// §8's recommendations rail — inline in the video payload, so no extra request
// ---------------------------------------------------------------------------

/**
 * A rail tile is ~168 px wide, so 320 px covers a 2× display without paying for `maxres` —
 * `pickImage` explains why smallest-sufficient is also the only honest choice here.
 */
const RAIL_THUMBNAIL_MIN_WIDTH = 320;

/**
 * §8's rail, built from the listing view model that was lifted out of this file.
 *
 * 1f left `RailItem` here with a note saying it would move on the second caller; the feed grid is
 * that caller, so the rail is now one configuration of `toListingItems` rather than a parallel copy
 * of the same four decisions. Two of its arguments are the interesting ones:
 *
 * - **`absoluteDates: false`.** This endpoint's `published` is back-computed from `publishedText`, so
 *   every row carries the request's own time of day and "2 years ago" resolves to exactly two years
 *   before the request. Accurate to the unit displayed and no further, which makes "2 years ago" the
 *   honest rendering and "27 July 2024" an invention. `/popular` is the one feed where it is genuine.
 * - **no view count, and that is measured rather than forgotten.** `recommendedVideos[]` carries only
 *   `viewCountText` ("5M", in whatever locale the instance felt like), which is exactly the field R7
 *   tells core to drop, and there is no numeric `viewCount` to format instead. So the rows come back
 *   with `viewCountText: null` on their own — no special case needed here, because "the record does
 *   not know" is the shape the model already speaks.
 */
export function toRailItems(
  videos: readonly VideoSummary[],
  nowEpochSec: number,
  progress: ProgressLookup,
): readonly ListingItem[] {
  return toListingItems(videos, {
    nowEpochSec,
    thumbnailMinWidth: RAIL_THUMBNAIL_MIN_WIDTH,
    absoluteDates: false,
    // 4b — the rail is where a progress bar earns the most: it is the list a reader clicks through,
    // so "I have seen that one" is the question every tile is being asked.
    progress,
  });
}

/** The rail paging: at most this many recommendations at once, prev/next below the window. */
const RAIL_PAGE_SIZE = 4;

export interface RailPager {
  readonly prevEnabled: boolean;
  readonly nextEnabled: boolean;
  /** "2 / 3" — where the window is. */
  readonly pageText: string;
}

export interface RailPageView {
  readonly items: readonly ListingItem[];
  /** `null` while everything fits in one window — a pager over four rows would be furniture. */
  readonly pager: RailPager | null;
  /** The page actually shown — the input clamped, so the buttons step from where the window is. */
  readonly page: number;
}

/**
 * The window the rail shows: at most four at once. The page is clamped rather than trusted —
 * the rail is twelve rows on every video sampled, but a model that renders an empty window because
 * state outlived a shorter list would be this function's fault, not the caller's.
 */
export function railPage(items: readonly ListingItem[], page: number): RailPageView {
  const pages = Math.max(1, Math.ceil(items.length / RAIL_PAGE_SIZE));
  const current = Math.min(Math.max(page, 0), pages - 1);
  const start = current * RAIL_PAGE_SIZE;
  return {
    items: items.slice(start, start + RAIL_PAGE_SIZE),
    pager:
      pages === 1
        ? null
        : {
            prevEnabled: current > 0,
            nextEnabled: current < pages - 1,
            pageText: text().watch.rail.pageOf(current + 1, pages),
          },
    page: current,
  };
}

// ---------------------------------------------------------------------------
// The list panel — the side column's third section, above chapters
// ---------------------------------------------------------------------------

/**
 * One entry in the panel. Deliberately thinner than the playlist *page*'s row: no selection and no
 * hover preview in a side column four rows tall — what a reader needs here is the position, the
 * picture, the name, the channel behind it and whether this is the one playing.
 *
 * Every row the panel draws is a **queue** row (`queue/model.ts`), whichever list the reader has open:
 * a `list` in the address makes the queue that playlist, so the two used to be separate row sources
 * and are now one.
 *
 * The watched treatment it carries, and the rail below it carries the same marks for the same
 * reason: a list a reader walks through is exactly where "I have seen that one" is the question every
 * row is being asked, and two lists an inch apart where only one of them answers it reads as a bug in
 * the other.
 */
export interface WatchPlaylistRow {
  readonly key: string;
  readonly videoId: string;
  /**
   * What a removal addresses: the row's **queue key**, which the loader maps back to the playlist's
   * own `indexId` before it sends anything (`screens/watch/useWatchPlaylist.ts`). `null` where the row
   * cannot be removed at all.
   *
   * Deliberately *not* called `indexId` since the queue started using this panel (2026-08-07): the
   * key a row is addressed by and the key the endpoint accepts are two different things, and a field
   * named after the second would be a lie about every row of a list that lives in this tab.
   */
  readonly removeKey: string | null;
  /**
   * 1-based, as displayed. The playlist's own `index` plus one while the queue is a window on a
   * playlist — so it survives the dedupe fold and stays right on a run that starts partway down the
   * list — and the row's place in the queue otherwise.
   */
  readonly position: number;
  /**
   * Where a press on the row goes. A linked row carries `list` and `index`, so it keeps the playlist
   * context; a row of the queue's own is a plain `/watch?v=` (`queue/model.ts`'s `queueEntryHref`).
   */
  readonly href: string;
  readonly title: string;
  readonly author: string | null;
  /** The channel's page — the name is a link here for the reason it is on every card. */
  readonly authorHref: string | null;
  readonly thumbnailUrl: string | null;
  readonly badge: ListingBadge | null;
  readonly durationSec: number | null;
  /**
   * The row the queue's cursor is on, which is what the list is playing. Exactly one row has it, and
   * none does while the run has not reached the playing entry yet or the list has played out.
   */
  readonly current: boolean;
  /** A row the endpoint blanked (6.9 % of all rows) — kept, so the numbering stays truthful. */
  readonly unavailable: boolean;
  /**
   * The watched word, for the half of the treatment a screen reader can hear: the bar and the mark
   * both ride inside a frame that is `aria-hidden`, so without this the row would be silent about a
   * state the screen is stating (`listing/model.ts`).
   */
  readonly marker: ListingMarker | null;
  /** The bar along the thumbnail's bottom edge. `null` where there is no stored position to draw. */
  readonly progress: ListingProgress | null;
  /**
   * The faded row and the "Watched" mark on the picture.
   *
   * **Never the playing row**, whatever the history says, and that is the one place this list differs
   * from a grid: the video a reader is watching right now is the last thing to fade away from them,
   * and a "Watched" chip over it would be answering a question nobody is asking about it.
   */
  readonly watched: boolean;
}

/**
 * The panel's window: **four rows** at a time, walked with the rail's own pager (six before,
 * and the panel sits above a rail that shows four, where two lists of different heights in one
 * column read as an accident).
 */
export const WATCH_PLAYLIST_PAGE_SIZE = 4;

export interface WatchPlaylistPageView {
  readonly rows: readonly WatchPlaylistRow[];
  readonly pager: RailPager | null;
  readonly page: number;
}

/**
 * The window the panel shows — the rail's pager over the rail's own four rows. The page is clamped
 * rather than trusted for `railPage`'s reason: a playlist that shrank under a stored page number
 * must render its last window, not an empty one. A removal is one of the ways it shrinks.
 */
export function watchPlaylistPage(
  rows: readonly WatchPlaylistRow[],
  page: number,
): WatchPlaylistPageView {
  const pages = Math.max(1, Math.ceil(rows.length / WATCH_PLAYLIST_PAGE_SIZE));
  const current = Math.min(Math.max(page, 0), pages - 1);
  const start = current * WATCH_PLAYLIST_PAGE_SIZE;
  return {
    rows: rows.slice(start, start + WATCH_PLAYLIST_PAGE_SIZE),
    pager:
      pages === 1
        ? null
        : {
            prevEnabled: current > 0,
            nextEnabled: current < pages - 1,
            pageText: text().watch.rail.pageOf(current + 1, pages),
          },
    page: current,
  };
}

/**
 * Which window holds the playing entry — the page the panel opens on, so "currently playing,
 * visible on load" costs no scrolling and no measurement. `0` when the entry is not in what has
 * been loaded yet, which is the honest answer while the walk to it is still going.
 */
export function watchPlaylistPageOf(rows: readonly WatchPlaylistRow[]): number {
  const at = rows.findIndex((row) => row.current);
  return at === -1 ? 0 : Math.floor(at / WATCH_PLAYLIST_PAGE_SIZE);
}

/**
 * The folded entries as the rows of a queue that is a window on this playlist — what the loader
 * projects into the store (`screens/watch/useWatchPlaylist.ts`).
 *
 * The **wire's own title** goes in, blank and all. A row the endpoint blanked is kept so the numbering
 * stays truthful (§6, 6.9 % of all rows), and the sentence that stands in for the missing title is the
 * panel's to draw rather than the store's to keep — a stored list holds records, not copy.
 *
 * `index` becomes `playlistIndex`, which is what the row's number and its route are built from, and
 * `indexId` rides along as the key a removal has to send (§7i: it is the only key the endpoint accepts,
 * and a mirrored YouTube list's rows carry none).
 */
export function toQueueLinkedRows(entries: readonly PlaylistEntry[]): readonly QueueLinkedRow[] {
  return entries.map((entry): QueueLinkedRow => {
    const video = entry.video;
    const gone = playlistEntryUnavailable(video);
    const route = gone ? null : channelRouteOf(video);

    return {
      videoId: video.videoId,
      title: video.title,
      // The named state rather than a bare blank test — §6's third row shape, a healthy video whose
      // channel this endpoint simply did not scrape, on one row in nine.
      author: gone || playlistEntryAuthorMissing(video) ? null : video.author,
      authorHref: route === null ? null : formatRoute(route),
      thumbnailUrl: gone
        ? null
        : (pickImage(video.thumbnails, { minWidth: RAIL_THUMBNAIL_MIN_WIDTH, widescreen: true })
            ?.url ?? null),
      durationSec: !gone && video.lengthSeconds > 0 ? video.lengthSeconds : null,
      indexId: entry.indexId,
      playlistIndex: entry.index,
    };
  });
}

// ---------------------------------------------------------------------------
// §8's jump-out links — the "Watch on" menu
// ---------------------------------------------------------------------------

/** Which player is actually driving the picture right now. */
export type ActivePlayerSource = 'visious' | 'youtube' | 'peertube';

/**
 * Where the PeerTube rows' own search is — this menu's only stateful input. The search runs once,
 * when the dropdown opens (2026-08-03), rather than on a row's own click, so by the time a reader
 * can press one the answer is usually already in: `found` carries the match's own instance host
 * (the dropdown's right-hand label, since a federated hit can live anywhere), and only a `found`
 * row is enabled at all — pressing it acts on the cached match rather than searching again.
 *
 * `no-info` is the state no search ever reaches (2026-08-06): the title and the length are what a
 * PeerTube search *is*, and a video whose details never arrived — a failed load, which is exactly
 * where the fallback is worth offering — has neither. It is derived rather than stored
 * (`useWatchScreen`), so a row that reads "No video info" while the video is still loading starts a
 * search of its own the moment the details land.
 */
export type PeerTubeSwitchState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'no-info' }
  | { readonly kind: 'found'; readonly instanceHost: string };

const SWITCH_YOUTUBE_ROW_ID = 'switch-youtube';
const SWITCH_PEERTUBE_ROW_ID = 'switch-peertube';
/** The way back, which either destination's row becomes while it is the one driving the picture. */
const SWITCH_BACK_ROW_ID = 'switch-visious';

const SWITCH_ROW_IDS: Readonly<Record<string, ActivePlayerSource>> = {
  [SWITCH_YOUTUBE_ROW_ID]: 'youtube',
  [SWITCH_PEERTUBE_ROW_ID]: 'peertube',
  [SWITCH_BACK_ROW_ID]: 'visious',
};

/** `open-` plus the destination. Which one it is belongs to the `<a>`, so nothing reads it back. */
const OPEN_ROW_PREFIX = 'open-';

/** What a press on a row of {@link watchOnMenuFor}'s menu meant. */
export type WatchOnRow =
  | { readonly kind: 'switch'; readonly target: ActivePlayerSource }
  | { readonly kind: 'open' }
  | { readonly kind: 'timestamp' };

/**
 * Reads a row id back into the act it stands for, and is the only place either spelling is written.
 *
 * The menu offers the same three destinations twice under two different verbs, so a target alone
 * cannot say what a press meant — "YouTube" is a player swap in one section and a link in the
 * other. The id therefore names the section as well, and both the hook (which acts on a press) and
 * the view (which decides whether the menu stays open for it) come back through here.
 */
export function watchOnRowOf(itemId: string): WatchOnRow | null {
  const target = SWITCH_ROW_IDS[itemId];
  if (target !== undefined) return { kind: 'switch', target };
  if (itemId === TIMESTAMP_ROW_ID) return { kind: 'timestamp' };
  return itemId.startsWith(OPEN_ROW_PREFIX) ? { kind: 'open' } : null;
}

/**
 * **One dropdown over both ways out of this player**: the destinations that replace the picture
 * where it stands, the destinations that are links to the video somewhere else, and under them the
 * one checkbox that decides what those links carry. Two triggers used to sit side by side saying
 * "Watch on" and "Switch to", which is the same promise twice and left the difference between them
 * to be learnt by pressing one; the section headings state it instead.
 *
 * The **links** are built from the route rather than from the video — so they exist when the
 * *video* does not. A `null` config — §3 resolved no instance — still yields the YouTube one, and
 * the failure states are where a reader wants them most: this instance answering 404 or handing
 * back the wrong body (API reference §3) says nothing about whether YouTube still has the video.
 *
 * `list` and `index` ride through untouched (§8 — `formatRoute` writes them back out), but the
 * timestamp belongs to the checkbox row: checked — the default, because "watch this elsewhere"
 * almost always means "from where I am" — the links carry the current playhead, unchecked they
 * carry no `t` at all. A playhead of 0:00 also writes no `t` (it is the same instruction), which
 * keeps the failure states — where nothing has played and the position the caller can offer falls
 * back to the route's own — pointing exactly where the route said. The row is absent on a live
 * stream: a position there names a moment in a window that has already moved on.
 *
 * `newTab` is what /embed asks for (2026-09-14): the links there land on the destination's **watch**
 * page and open a tab of their own. The menu used to build them from the embed route, on the reading
 * that jumping out of an iframe means swapping the player in place — but the reader who follows one
 * has given up on this player, and what they get in the embedder's frame is a second bare player
 * with no title, no description and no way on. A new tab leaves the page they were reading intact
 * and puts the whole video page in front of them, which is the same thing the band's title link
 * does. `formatRoute`'s `?start=` spelling rides along, honoured by both destinations.
 *
 * The **switches** are actions rather than links, and each is one setting apiece: a reader who has
 * turned neither on gets no section at all, since dead knobs for a fallback nobody asked for are
 * noise. That is also why the headings come and go with it — with one section left they would name
 * the only rows there are.
 *
 * The replace-in-place rule (2026-08-03, decided with the reader): whichever destination is
 * currently driving the picture does not get a second row of its own — its row *becomes* the way
 * back to Visious, and the *other* destination (if enabled) keeps its own row unchanged, so a
 * reader can jump straight from one fallback to the other without detouring through Visious.
 *
 * **PeerTube is offered in both sections** (2026-08-06), and it is the only destination that is not
 * a domain swap: there is no PeerTube address for a YouTube id, so both rows are whatever the
 * title-and-length search found. They therefore wear the search's own states rather than a link's —
 * a spinner while it runs, "No video found" on a miss, the match's own instance host on a hit — and
 * the link is a link at all only once there is a hit, a row with no `href` rendering as the
 * disabled button `Menu` draws for an action. Absent from both sections unless the fallback is
 * switched on, since nothing may search a third-party service the reader did not ask for.
 */
export function watchOnMenuFor(
  config: InstanceConfig | null,
  route: Extract<Route, { kind: 'watch' }>,
  options: {
    readonly includeTimestamp: boolean;
    readonly positionSec: number;
    readonly live: boolean;
    /** Every Open site row opens a tab of its own — /embed's whole menu, and nowhere else. */
    readonly newTab: boolean;
    /** `null` while the PeerTube fallback is off — neither section offers it then. */
    readonly peerTube: {
      readonly state: PeerTubeSwitchState;
      readonly match: PeerTubeSearchResult | null;
    } | null;
    /** The YouTube-embed escape hatch's own switch. It gates the swap, never the link. */
    readonly youtubeSwitch: boolean;
    readonly activeSource: ActivePlayerSource;
  },
): MenuModel {
  const words = text().watch.watchOn;
  const positionSec = Math.max(0, Math.floor(options.positionSec));
  const startSec = options.live || !options.includeTimestamp || positionSec === 0 ? null : positionSec;
  const linkRoute: Route = options.live ? route : { ...route, startSec };

  const switches = switchPlayerRows(options.activeSource, options.youtubeSwitch, options.peerTube);
  const links: MenuItemModel[] = jumpOutLinks(config, linkRoute).map((link) => ({
    id: `${OPEN_ROW_PREFIX}${link.target}`,
    // The destination's name alone — the host beside it said the same thing twice.
    label: words.targets[link.target],
    detail: null,
    selected: false,
    href: link.url,
    newTab: options.newTab,
  }));

  const peerTubeLink = peerTubeOpenRow(options.peerTube, options.newTab, startSec);
  if (peerTubeLink !== null) links.push(peerTubeLink);

  const headed = switches.length > 0;
  const sections: MenuSectionModel[] = [];
  if (headed) sections.push({ title: words.switchPlayer, items: switches });
  sections.push({ title: headed ? words.openSite : null, items: links });

  if (!options.live) {
    sections.push({
      title: null,
      items: [
        {
          id: TIMESTAMP_ROW_ID,
          label: words.atTime(formatDuration(positionSec)),
          detail: null,
          selected: options.includeTimestamp,
          checkbox: true,
        },
      ],
    });
  }

  return { label: words.label, value: '', valueShort: null, sections, note: null };
}

/** The Switch player section's rows, or none at all when neither escape hatch is switched on. */
function switchPlayerRows(
  activeSource: ActivePlayerSource,
  youtubeSwitch: boolean,
  peerTube: {
    readonly state: PeerTubeSwitchState;
    readonly match: PeerTubeSearchResult | null;
  } | null,
): MenuItemModel[] {
  const words = text().watch.watchOn;
  const back: MenuItemModel = {
    id: SWITCH_BACK_ROW_ID,
    label: words.visious,
    detail: null,
    selected: false,
    action: true,
  };
  const items: MenuItemModel[] = [];

  if (youtubeSwitch) {
    items.push(
      activeSource === 'youtube'
        ? back
        : {
            id: SWITCH_YOUTUBE_ROW_ID,
            label: words.targets.youtube,
            detail: null,
            selected: false,
            action: true,
          },
    );
  }

  if (peerTube !== null) {
    items.push(
      activeSource === 'peertube'
        ? back
        : peerTube.state.kind === 'found'
          ? {
              id: SWITCH_PEERTUBE_ROW_ID,
              label: words.targets.peertube,
              detail: peerTube.state.instanceHost,
              selected: false,
              action: true,
            }
          : peerTubeWaitingRow(SWITCH_PEERTUBE_ROW_ID, peerTube.state, words),
    );
  }

  return items;
}

/**
 * The Open site section's PeerTube row, or `null` when the fallback is off.
 *
 * The match's own watch page, on /watch and on /embed alike (2026-09-14) — the destination rows all
 * lead to a whole video page now, and this one had the same embed-page address the other two did. A
 * `found` state with no address left to build (a payload carrying neither `url` nor a usable host)
 * falls back to the disabled row, because a dead link is worse than an honest refusal.
 */
function peerTubeOpenRow(
  peerTube: {
    readonly state: PeerTubeSwitchState;
    readonly match: PeerTubeSearchResult | null;
  } | null,
  newTab: boolean,
  startSec: number | null,
): MenuItemModel | null {
  if (peerTube === null) return null;
  const words = text().watch.watchOn;
  const { state, match } = peerTube;
  const id = `${OPEN_ROW_PREFIX}peertube`;

  const url =
    state.kind === 'found' && match !== null ? peerTubeWatchUrl(match, startSec) : null;

  if (url === null) return peerTubeWaitingRow(id, state, words);

  return {
    id,
    label: words.targets.peertube,
    detail: state.kind === 'found' ? state.instanceHost : null,
    selected: false,
    href: url,
    newTab,
  };
}

/**
 * The row a PeerTube destination wears while there is nothing to act on yet: a spinner during the
 * search, and otherwise the reason the row is dead. Three of them draw it — the two Watch on
 * sections and Share link — each with its own id and its own words, so the words are a parameter
 * and the states are not: a row that said "No video found" in one place and nothing in another
 * would be describing the same search twice.
 */
function peerTubeWaitingRow(
  id: string,
  state: PeerTubeSwitchState,
  words: {
    readonly targets: { readonly peertube: string };
    readonly peerTubeNotFound: string;
    readonly peerTubeNoInfo: string;
  },
): MenuItemModel {
  return {
    id,
    label: words.targets.peertube,
    detail: peerTubeWaitingDetail(state, words),
    selected: false,
    action: true,
    disabled: true,
    busy: state.kind === 'searching',
  };
}

function peerTubeWaitingDetail(
  state: PeerTubeSwitchState,
  words: { readonly peerTubeNotFound: string; readonly peerTubeNoInfo: string },
): string | null {
  switch (state.kind) {
    case 'unavailable':
      return words.peerTubeNotFound;
    case 'no-info':
      return words.peerTubeNoInfo;
    case 'idle':
    case 'searching':
    case 'found':
      return null;
  }
}

// ---------------------------------------------------------------------------
// Share (2026-08-02)
// ---------------------------------------------------------------------------

/**
 * The three places a copied link can be *derived* from the route. `instance` is offered only when §3
 * resolved one; PeerTube is a fourth destination and deliberately not one of these, since its
 * address is a search result rather than a domain swap (see {@link PEERTUBE_ROW_ID}).
 */
export type ShareTarget = 'visious' | 'instance' | 'youtube';

/**
 * The timestamp checkbox row both the Share and the Watch on dropdowns carry. Exported so the view
 * knows which select keeps its menu open.
 */
export const TIMESTAMP_ROW_ID = 'timestamp';

/**
 * The Share menu's PeerTube row. Exported because the menu has to tell that row apart from the ones
 * around it: every other destination there is a domain swap of the route, and this one's address is
 * whatever the search found. (The Watch on menu names its own rows by section — `watchOnRowOf`.)
 */
export const PEERTUBE_ROW_ID = 'peertube';

const SHARE_TARGETS: readonly ShareTarget[] = ['visious', 'instance', 'youtube'];

/**
 * The video alone — no `list`, no `index`. A share link is "watch this", not "walk my playlist";
 * the recipient gets the video, and the timestamp only when the reader asked for it.
 */
function shareRoute(videoId: string, startSec: number | null): Route {
  return { kind: 'watch', videoId, startSec, playlistId: null, index: null };
}

/**
 * The address the reader is left on when they let go of the playlist they were watching through — the
 * panel's Clear, for a list that lives on the instance.
 *
 * It is {@link shareRoute}'s route, and deliberately the same one: "the video alone" is the same four
 * fields whether it is being handed to somebody else or being kept. Two of them decide whether this
 * works at all.
 *
 * **`startSec` is carried through rather than dropped.** The player's mount effect depends on where it
 * was told to start, and that resolves to the route's `t` *or else* the stored resume position — so a
 * route that loses its `t` hands the player a different start, reloads the manifest and drops the
 * picture back to a position the reader left an hour ago. Letting go of a list must not touch the
 * playback (`playback/model.ts` records the same failure for Maximize).
 *
 * **`index` goes with `playlistId`.** `formatRoute` writes an index whenever it has one, `list` or no
 * `list`, so a route left holding one keeps emitting `?index=3` and parses straight back into a
 * playlist position for a playlist that is no longer named.
 */
export function watchWithoutListHref(videoId: string, startSec: number | null): string {
  return formatRoute(shareRoute(videoId, startSec));
}

/**
 * The same address the other way round: this video, watched through that playlist — where a reader is
 * left once the queue they built has been saved into one.
 *
 * **Linking goes through the address rather than by hand**, which is what makes this function the whole
 * of the act. Arriving at a `list` route is what the panel's loader answers by reading the playlist and
 * projecting it into the queue, so the rows the reader keeps are the playlist's own rows in the
 * playlist's own order. Building the link in the store instead would have to invent a `playlistIndex`
 * for every row the save skipped as already present, and those positions are the playlist's to state.
 *
 * `startSec` is carried through for {@link watchWithoutListHref}'s reason: the player's mount effect
 * depends on where it was told to start, so a route that loses its `t` drops the picture back to the
 * resume position. `index` is the row the playing video landed at where the save learned it from the
 * `201` (§7i returns the whole entry row) and `null` where the playlist already held that video: the
 * position is only ever the read's opening guess, since the run is anchored on it and the cursor is
 * placed by video id either way (`core/queue/model.ts`'s `placeCursor`).
 */
export function watchWithListHref(
  videoId: string,
  startSec: number | null,
  playlistId: string,
  index: number | null,
): string {
  return formatRoute({ kind: 'watch', videoId, startSec, playlistId, index });
}

/**
 * `null` for `instance` with no instance resolved — the caller omits the row rather than pointing a
 * reader at a host that does not exist. The Visious link is this deployment's own address, which is
 * the one origin `links/` cannot know (R2 keeps core origin-free); reading it off `location` here
 * is the web platform naming itself, not an instance URL escaping core.
 */
export function shareUrlFor(
  target: ShareTarget,
  config: InstanceConfig | null,
  videoId: string,
  startSec: number | null,
): string | null {
  const route = shareRoute(videoId, startSec);
  switch (target) {
    case 'visious':
      return new URL(formatRoute(route), globalThis.location.origin).toString();
    case 'instance':
      return config === null ? null : instanceRouteUrl(config, route);
    case 'youtube':
      return youtubeRouteUrl(route);
  }
}

/**
 * The Share dropdown: the destinations, and under them one checkbox row that bakes the current
 * playhead into whichever link is copied next. The row's label carries the time it would copy —
 * "Start at 12:34" — and is absent entirely on a live stream, where a position names a moment in a
 * window that has already moved on.
 *
 * **PeerTube is the fourth destination** (2026-08-06), under the same rule the Watch-on menu's own
 * PeerTube row follows: the address is whatever the title-and-length search found, so the row wears
 * the search's states — a spinner while it runs, "No video found" on a miss, the match's own instance
 * host on a hit — and only a hit can be pressed. It stays an action row in every state, because every
 * row here copies rather than navigates; what a press copies is the caller's to build
 * ({@link PEERTUBE_ROW_ID}), the match being the one address no route can produce.
 */
export function shareMenuFor(
  config: InstanceConfig | null,
  options: {
    readonly includeTimestamp: boolean;
    readonly positionSec: number;
    readonly live: boolean;
    /** `null` while the PeerTube fallback is off — the row is then not offered at all. */
    readonly peerTube: {
      readonly state: PeerTubeSwitchState;
      readonly match: PeerTubeSearchResult | null;
    } | null;
  },
): MenuModel {
  const words = text().watch.share;
  const targets: MenuItemModel[] = SHARE_TARGETS.filter(
    (target) => target !== 'instance' || config !== null,
  ).map((target) => ({
    id: target,
    label: words.targets[target],
    detail: null,
    selected: false,
    action: true,
  }));

  const peerTubeRow = sharePeerTubeRow(options.peerTube);
  if (peerTubeRow !== null) targets.push(peerTubeRow);

  const sections: MenuSectionModel[] =
    options.live === true
      ? [{ title: null, items: targets }]
      : [
          { title: null, items: targets },
          {
            title: null,
            items: [
              {
                id: TIMESTAMP_ROW_ID,
                label: words.atTime(formatDuration(Math.floor(options.positionSec))),
                detail: null,
                selected: options.includeTimestamp,
                checkbox: true,
              },
            ],
          },
        ];

  return { label: words.label, value: '', valueShort: null, sections, note: null };
}

/**
 * The Share menu's PeerTube row, or `null` when the fallback is off.
 *
 * A hit is an ordinary action row carrying the match's host: the address is assembled at the press
 * (`sharePeerTubeUrl`), since the timestamp checkbox under the destinations decides it and a link
 * built here would be the one this menu had when it opened.
 */
function sharePeerTubeRow(
  peerTube: {
    readonly state: PeerTubeSwitchState;
    readonly match: PeerTubeSearchResult | null;
  } | null,
): MenuItemModel | null {
  if (peerTube === null) return null;
  const words = text().watch.share;
  const { state, match } = peerTube;

  if (state.kind !== 'found' || match === null) return peerTubeWaitingRow(PEERTUBE_ROW_ID, state, words);

  return {
    id: PEERTUBE_ROW_ID,
    label: words.targets.peertube,
    detail: state.instanceHost,
    selected: false,
    action: true,
  };
}

/** The transient line under the actions row after a copy — the confirmation, or the refusal. */
export function shareOutcomeText(outcome: 'copied' | 'failed'): string {
  const words = text().watch.share;
  return outcome === 'copied' ? words.copied : words.copyFailed;
}

// ---------------------------------------------------------------------------
// Transport controls
// ---------------------------------------------------------------------------

export interface TransportState {
  readonly playing: boolean;
  /**
   * The video finished. Read by the playlist auto-advance, which is the reason it is a field
   * rather than a comparison at the call site: a paused video on its last frame reports the same
   * position and the same `playing: false`, and only one of the two means "go to the next entry".
   */
  readonly ended: boolean;
  readonly muted: boolean;
  readonly volume: number;
  readonly currentSec: number;
  readonly durationSec: number;
  readonly bufferedSec: number;
  readonly currentText: string;
  readonly durationText: string;
  /** 0–1. `0` rather than `NaN` before the duration is known, so the bar never renders undefined. */
  readonly progress: number;
  readonly bufferedProgress: number;
  /** The chapter the playhead is inside, by index. Names the current chapter under the bar. */
  readonly chapterIndex: number | null;
  readonly playbackRate: number;
}

export interface TransportSample {
  readonly currentTimeSec: number;
  readonly durationSec: number;
  readonly bufferedEndSec: number;
  readonly paused: boolean;
  /** The element ran to the end and stopped — not the same as paused on the last frame. */
  readonly ended: boolean;
  readonly muted: boolean;
  readonly volume: number;
  readonly playbackRate: number;
}

export const IDLE_TRANSPORT: TransportState = {
  playing: false,
  ended: false,
  muted: false,
  volume: 1,
  currentSec: 0,
  durationSec: 0,
  bufferedSec: 0,
  currentText: '0:00',
  durationText: '0:00',
  progress: 0,
  bufferedProgress: 0,
  chapterIndex: null,
  playbackRate: 1,
};

export function toTransportState(
  sample: TransportSample,
  chapters: readonly Chapter[],
  /** A live stream reports `Infinity`; the plan's duration is the honest number for a DVR window. */
  fallbackDurationSec: number | null,
): TransportState {
  const duration =
    Number.isFinite(sample.durationSec) && sample.durationSec > 0
      ? sample.durationSec
      : (fallbackDurationSec ?? 0);

  return {
    playing: !sample.paused,
    ended: sample.ended,
    muted: sample.muted,
    volume: sample.volume,
    currentSec: sample.currentTimeSec,
    durationSec: duration,
    bufferedSec: sample.bufferedEndSec,
    currentText: formatDuration(sample.currentTimeSec),
    durationText: formatDuration(duration),
    progress: positionFraction(sample.currentTimeSec, duration),
    bufferedProgress: positionFraction(sample.bufferedEndSec, duration),
    chapterIndex: chapterIndexOf(chapters, sample.currentTimeSec),
    playbackRate: sample.playbackRate,
  };
}

/** Kept as a named export because it reads better at the call sites than core's qualified name. */
export const chapterIndexAt = chapterIndexOf;

// ---------------------------------------------------------------------------
// The bar (§7 "Chapters on the bar")
// ---------------------------------------------------------------------------

/**
 * What the scrub bar paints. `position` is the *displayed* playhead, which is not always the
 * playing one: while a drag is in progress the bar has to follow the thumb rather than the video,
 * or the handle springs back to the last decoded frame on every pointer move.
 */
export function barSegments(
  chapters: readonly Chapter[],
  transport: TransportState,
  scrubbingSec: number | null,
): readonly TimelineSegment[] {
  const position = scrubbingSec ?? transport.currentSec;
  return timelineSegments(chapters, transport.durationSec, position, transport.bufferedSec);
}

/**
 * The native range's own four numbers, over the same displayed position `barSegments` paints.
 *
 * Its own function because the quantisation inside it is load-bearing rather than tidy — see
 * `scrubRange`. A raw `video.currentTime` is a value the element cannot store, and the disagreement
 * that follows is what undid a seek 8 ms after it landed.
 */
export function barRange(transport: TransportState, scrubbingSec: number | null): ScrubRange {
  return scrubRange(scrubbingSec ?? transport.currentSec, transport.durationSec);
}

/**
 * The SponsorBlock stripes inside one timeline cell — core's own fractions, re-exported so the
 * chrome renders them without importing core. The stripes ride the *cells* rather than the whole
 * track because the cells have real gaps between them: a global fraction would drift against the
 * chapters by the width of every gap it crosses.
 */
export function barSponsorMarks(
  cell: TimelineSegment,
  segments: readonly ResolvedSponsorSegment[],
): readonly SponsorBarMark[] {
  return sponsorMarksFor(cell, segments);
}

/**
 * What the knob wears (2026-08-01): the colour of the drawn segment under the displayed
 * playhead — the dragged position while a drag is in progress, `barSegments`' own rule — and
 * `null` anywhere the bar shows no stripe, which hands the knob back its usual colour.
 */
export function barKnobColor(
  transport: TransportState,
  scrubbingSec: number | null,
  segments: readonly ResolvedSponsorSegment[],
): string | null {
  return sponsorMarkAt(segments, scrubbingSec ?? transport.currentSec)?.color ?? null;
}

/** A chapter row's badge, worded: the category name on the row, the whole story on hover. */
export interface ChapterBadgeView {
  readonly label: string;
  readonly color: string;
  readonly title: string;
}

/**
 * One badge per chapter at most (decided 2026-08-01: the chapters panel only) — core measures the
 * ≥ 75 % coverage, this names it. Index-aligned with `WatchVideo.chapters`.
 */
export function chapterBadgeViews(
  chapters: readonly Chapter[],
  segments: readonly ResolvedSponsorSegment[],
): readonly (ChapterBadgeView | null)[] {
  const names = text().sponsorBlock.category;
  const words = text().watch.sponsor;
  return chapterSponsorBadges(chapters, segments).map((badge) => {
    if (badge === null) return null;
    const label = names[badge.category];
    return { label, color: badge.color, title: words.badgeTitle(label) };
  });
}

/**
 * What the player toast says and offers. The *state* (which segment, which kind) lives in the
 * hook; this is only the wording, so the sentence and the button label stay reviewable as copy.
 */
export interface SponsorToastView {
  readonly kind: 'skipped' | 'offer';
  readonly text: string;
  readonly actionLabel: string;
}

export function sponsorToastView(
  kind: 'skipped' | 'offer',
  segment: ResolvedSponsorSegment,
): SponsorToastView {
  const words = text().watch.sponsor;
  const name = text().sponsorBlock.category[segment.category];
  return kind === 'skipped'
    ? { kind, text: words.skipped(name), actionLabel: words.unskip }
    : { kind, text: name, actionLabel: words.skip };
}

/**
 * What a skip says while it is happening — one view over core's {@link SkipRun}, wearing two
 * faces because the two ways of asking for a skip are looked at from different distances.
 *
 * A **double tap** is a finger on the picture, and the answer belongs where the finger is: a disc on
 * that side of the frame, printing the run's total and nothing else — the side it is on says which
 * way, so a word there would be the third thing saying it. A **control press** is a pointer on the
 * bar, where a disc over the middle of the video would be a large answer to a small act, so it takes
 * the SponsorBlock toast's clothes instead and says the direction in words.
 *
 * `origin` is the *act*, not the device: the keyboard's `←`/`→` and `J`/`L` are the bar's buttons
 * under another name, so they raise the toast too.
 */
export interface SkipFeedbackView {
  readonly origin: 'gesture' | 'control';
  readonly direction: 'back' | 'forward';
  /** The disc's one line: the run's total, unit short enough for a circle. */
  readonly discText: string;
  /** The toast's sentence, and the disc's accessible name. */
  readonly text: string;
  /**
   * When the **run** began — the disc's `key`, so a run of quick presses is one element whose number
   * goes up rather than a new element per press — "shouldn't flash … just stay there" was the ask. Keying on
   * the *last* press restarted the fade every time; keying on the wording alone has the opposite hole,
   * since a fresh run beginning after the joinable window but before the old disc has faded reads "10s"
   * exactly like the one on screen. The run's start is the only field that answers both.
   */
  readonly stamp: number;
  /**
   * The run's fade is over: the element is on its way out and unmounts when it lands. A flag rather
   * than a second animation on the same class, because a class that is *added* starts its animation
   * while a class React re-renders unchanged does not.
   */
  readonly leaving: boolean;
}

export function skipFeedbackView(
  run: SkipRun,
  origin: 'gesture' | 'control',
  leaving: boolean,
): SkipFeedbackView {
  const words = text().watch.chrome;
  return {
    origin,
    direction: run.direction,
    discText: words.skipDisc(run.totalSec),
    text: run.direction === 'back' ? words.skipBack(run.totalSec) : words.skipForward(run.totalSec),
    stamp: run.startedAtMs,
    leaving,
  };
}

/** What the tooltip above the bar says, and what the preview tile shows. */
export interface ScrubHover {
  /** 0–1 along the bar. The renderer positions the tooltip with it; it is not a time. */
  readonly fraction: number;
  readonly seconds: number;
  readonly timeText: string;
  /** `null` on an unchaptered video — the tooltip is then a time and a picture. */
  readonly chapterTitle: string | null;
  readonly preview: StoryboardPreview | null;
  /** The drawn segment under the hover, named — the tooltip's topmost line (2026-08-01). */
  readonly sponsor: { readonly name: string; readonly color: string } | null;
}

export function scrubHover(
  fraction: number,
  transport: TransportState,
  chapters: readonly Chapter[],
  cues: readonly StoryboardCue[],
  previewWidthPx: number,
  segments: readonly ResolvedSponsorSegment[],
): ScrubHover {
  const seconds = seekSecondsAt(fraction, transport.durationSec);
  const index = chapterIndexOf(chapters, seconds);
  const mark = sponsorMarkAt(segments, seconds);

  return {
    fraction: positionFraction(seconds, transport.durationSec),
    seconds,
    timeText: formatDuration(seconds),
    chapterTitle: index === null ? null : (chapters[index]?.title ?? null),
    // `storyboardCueAt` clamps past the end to the last cue rather than to nothing, so the preview
    // does not vanish in the final seconds of every video.
    preview: storyboardPreview(storyboardCueAt(cues, seconds * 1000), previewWidthPx),
    sponsor:
      mark === null ? null : { name: text().sponsorBlock.category[mark.category], color: mark.color },
  };
}

// The auto-hide rule moved to core (`playback/chrome.ts`) when the pointer-over-the-bar case was
// added: it is the same shape as the rest of that file — a decision over §7's chrome with no DOM in
// it — and it had no test here, because this file is not importable from `core-testkit`.

// ---------------------------------------------------------------------------
// The menus (§7's quality / audio language / subtitle / speed rows)
// ---------------------------------------------------------------------------

export type MenuId = 'quality' | 'speed' | 'audio' | 'subtitles';

/**
 * A player menu is a `MenuModel` (`ui/menuModel.ts`) plus the id that says which of §7's four rows it
 * is. The shape moved to `ui/` when the search filter row became the second caller — a
 * type-only import, so R13's "no DOM in a model" still holds.
 */
export interface PlayerMenu extends MenuModel {
  readonly id: MenuId;
}

const AUTO_ID = 'auto';
const OFF_ID = 'off';
const CODEC_AUTO_ID = 'codec:auto';
const CODEC_AVC_ID = 'codec:avc';

/**
 * The subtitle rows' id prefix while they are folded into the audio menu on the narrow bar. The two
 * menus address their rows by **language code**, so `en` means a dub in one and a caption track in the
 * other — merged without a prefix, choosing English subtitles would have switched the audio track.
 * `selectMenuItem` routes on this and hands the bare id to `withSubtitleSelection`.
 */
export const SUBTITLE_ROW_PREFIX = 'subtitles:';

/**
 * The same menu with nothing printed beside its icon. Below the `md` step the bar has room for
 * icons and little else — "1080p", "1.5×" and a language code are four controls' worth of text on a
 * 360 px line — and `valueShort: null` is already the vocabulary for it: the trigger draws the icon
 * alone, exactly as the subtitles control does when captions are off. The *accessible* name is
 * untouched, because it is built from `value`, so the control still announces "Quality: Auto (480p)".
 */
export function menuWithoutValue(menu: PlayerMenu): PlayerMenu;
export function menuWithoutValue(menu: PlayerMenu | null): PlayerMenu | null;
export function menuWithoutValue(menu: PlayerMenu | null): PlayerMenu | null {
  return menu === null ? menu : { ...menu, valueShort: null };
}

/**
 * Audio and subtitles as one control on the narrow bar: the audio rows, then the caption rows under
 * a heading of their own, behind the audio trigger's icon.
 *
 * Two menus become one *control*, not one list — the sections keep their own headings and the subtitle
 * ids keep {@link SUBTITLE_ROW_PREFIX} — because they are two independent choices that happen to be
 * about the same thing. Either side may be absent: a one-language video keeps its subtitles menu under
 * its own id and icon (the caller must not print an audio trigger for a menu with no audio in it), and
 * a video with no captions keeps the audio menu untouched.
 */
export function mergedAudioMenu(
  audio: PlayerMenu | null,
  subtitles: PlayerMenu | null,
): PlayerMenu | null {
  if (audio === null || subtitles === null) return audio ?? subtitles;

  const words = text().watch.menu;
  return {
    ...audio,
    label: words.audioAndSubtitles,
    // The closed control's value names both halves, in the order the sections run.
    value: words.audioAndSubtitlesValue(audio.value, subtitles.value),
    sections: [
      ...audio.sections,
      ...subtitles.sections.map((section) => ({
        // The audio rows come first and carry no heading, so this one names the boundary rather than
        // repeating what the section is: without it the caption languages read as more audio tracks.
        title: section.title ?? words.subtitles.label,
        items: section.items.map((item) => ({ ...item, id: `${SUBTITLE_ROW_PREFIX}${item.id}` })),
      })),
    ],
  };
}

function mbps(bandwidth: number): string | null {
  if (!Number.isFinite(bandwidth) || bandwidth <= 0) return null;
  return text().watch.menu.quality.bitrate(
    (bandwidth / 1_000_000).toFixed(bandwidth < 1_000_000 ? 2 : 1),
  );
}

/**
 * §7's quality row plus its "Force H.264" escape hatch.
 *
 * The codec lives in the same menu but its own section, because it is a *different axis*: Shaka
 * exposes exactly one codec family at a time (it drops the losing one when it filters the manifest —
 * 0d saw 6 AV1 tracks out of 12 representations), so the height list below it is the list *for the
 * family currently loaded*. Presenting them as one flat list would offer heights that only exist
 * after a reload nobody asked for.
 */
export function qualityMenu(
  tracks: readonly VideoTrackInfo[],
  preferences: PlaybackPreferences,
): PlayerMenu {
  const quality = preferences.quality;
  const pinnedHeight = quality.kind === 'pinned' ? quality.height : null;
  const decision = decideVideoTrack(quality, tracks);
  const forced = quality.codec === 'avc';

  /**
   * One row per **height**, not per (height, codec family) — which is what `qualityOptions`
   * faithfully reports, and which is one row too many for a menu.
   *
   * `SqcY0GlETPk` is the case that shows why: Shaka exposes 720p twice there, as H.264 at 1.6 Mbps
   * and AV1 at 1.2 Mbps. `SubtitlePref`'s problem again in a different costume — `VideoQualityPref`
   * pins a *height*, and `decideVideoTrack` resolves it by height then bandwidth, so both rows
   * select the same track. Two identical-looking rows where one is unreachable is worse than one
   * row. `qualityOptions` already sorts fattest-first within a height, so keeping the first entry
   * keeps the menu in step with what the selector would pick. Choosing a codec is the section below.
   */
  const words = text().watch.menu.quality;
  const seen = new Set<number>();
  const heights: MenuItemModel[] = [];
  for (const option of qualityOptions(tracks)) {
    if (seen.has(option.height)) continue;
    seen.add(option.height);
    heights.push({
      id: option.key,
      label: words.height(option.height),
      detail: mbps(option.bandwidth),
      selected: pinnedHeight === option.height,
    });
  }

  const active = tracks.find((track) => track.active);

  return {
    id: 'quality',
    label: words.label,
    value:
      pinnedHeight === null
        ? active?.height === undefined || active.height === null
          ? words.auto
          : words.autoAt(active.height)
        : words.height(pinnedHeight),
    // The bar prints the height alone — "1080p" — where the accessible name says "Auto (1080p)";
    // whether that height is pinned or adaptive is the menu's first row, not the bar's business.
    valueShort:
      pinnedHeight !== null
        ? words.height(pinnedHeight)
        : active?.height === undefined || active.height === null
          ? words.auto
          : words.height(active.height),
    sections: [
      {
        title: null,
        items: [
          {
            id: AUTO_ID,
            label: words.auto,
            detail: words.autoDetail,
            selected: quality.kind === 'auto',
          },
          ...heights,
        ],
      },
      {
        title: words.codecSection,
        items: [
          { id: CODEC_AUTO_ID, label: words.codecAuto, detail: null, selected: !forced },
          {
            id: CODEC_AVC_ID,
            label: words.codecForceAvc,
            detail: words.codecForceAvcDetail,
            selected: forced,
          },
        ],
      },
    ],
    note:
      forced && !decision.codecFamilyMatched
        ? words.noAvcNote
        : quality.kind === 'pinned' &&
            pinnedHeight !== null &&
            decision.reason !== 'exact' &&
            decision.reason !== 'adaptive'
          ? words.pinMissedNote(pinnedHeight)
          : null,
  };
}

/** Selecting a quality item. Returns the same object when nothing changed, so React can skip work. */
export function withQualitySelection(
  preferences: PlaybackPreferences,
  itemId: string,
  tracks: readonly VideoTrackInfo[],
): PlaybackPreferences {
  const codec = preferences.quality.codec;

  if (itemId === AUTO_ID) return { ...preferences, quality: { kind: 'auto', codec } };
  if (itemId === CODEC_AUTO_ID || itemId === CODEC_AVC_ID) {
    const next: CodecFamily | undefined = itemId === CODEC_AVC_ID ? 'avc' : undefined;
    // The codec is set on whichever arm is current, so forcing H.264 never silently pins the
    // resolution and turning it off never silently unpins one.
    const quality = preferences.quality;
    return {
      ...preferences,
      quality:
        quality.kind === 'pinned'
          ? { kind: 'pinned', height: quality.height, codec: next }
          : { kind: 'auto', maxHeight: quality.maxHeight, codec: next },
    };
  }

  const option = qualityOptions(tracks).find((candidate) => candidate.key === itemId);
  if (option === undefined) return preferences;
  return { ...preferences, quality: { kind: 'pinned', height: option.height, codec } };
}

/**
 * §7's audio row. `null` when there is nothing to choose: one track is not a menu, and a control
 * that only ever shows its own single option is worse than no control.
 *
 * The menu lists **one row per language** whichever rendition is on (revised 2026-08-01 —
 * `useStableVolume` used to *add* the DRC set as extra rows, which doubled every language). The
 * stable-volume switch is its own section at the bottom, where the quality menu keeps its codec
 * toggle: `preferences.audio.drc` decides which rendition every row resolves to, and
 * `audioOptions` falls back to the rendition that exists for a language with no twin.
 *
 * **There is no separate "Original" entry**: the original track is one of the rows, so a first
 * row that resolves to it was the same choice twice, wearing two names. Instead the original's own row
 * carries {@link AUTO_ID} — choosing it stores `auto`, "the original, whatever language that is" — and
 * is the selected one while the preference is `auto`. "Original"/"OG" survive only as the fallback for
 * a manifest whose original track carries no usable language tag.
 */
export function audioMenu(
  tracks: readonly AudioTrackInfo[],
  preferences: PlaybackPreferences,
): PlayerMenu | null {
  const pref = preferences.audio;
  const stable = pref.drc === true;
  const options = audioOptions(tracks, { stableVolume: stable });
  // One language and no DRC twin is nothing to choose. One language *with* a twin keeps the menu:
  // the stable-volume switch below is then the only choice the video offers, and it is a real one.
  if (options.length < 2 && !tracks.some(isDrcOption)) return null;

  const selectedCode = pref.kind === 'lang' ? pref.code.toLowerCase() : null;
  const active = tracks.find((track) => track.active);
  // The original: what `auto` resolves to — `main`, not dub, in the rendition the switch asks for.
  const originalKey = decideAudioTrack(stable ? { kind: 'auto', drc: true } : { kind: 'auto' }, tracks)
    .trackKey;
  const original = tracks.find((track) => track.key === originalKey);

  // The original leads, where the retired "Original" entry sat — the MPD orders audio sets
  // alphabetically by language, which buries the original (`Role=main`) at the bottom of a
  // twenty-row menu. A stable partition, so the languages keep the manifest's own order.
  const ordered = [
    ...options.filter((track) => track.key === originalKey),
    ...options.filter((track) => track.key !== originalKey),
  ];

  const items: MenuItemModel[] = ordered.map((track) => {
    const isOriginal = track.key === originalKey;
    return {
      // The original row *is* the auto arm: choosing it means "the original", which stays true on
      // the next video, where an explicit language would mean its dub.
      id: isOriginal ? AUTO_ID : track.language,
      label: audioRowLabel(tracks, track, isOriginal),
      // The code on the row's far end, country and all — the original's row included, which
      // used to wear a sentence there instead of looking like the language row it is.
      detail: languageOrNull(track),
      selected: isOriginal
        ? pref.kind === 'auto' || selectedCode === track.language.toLowerCase()
        : selectedCode === track.language.toLowerCase(),
    };
  });

  const words = text().watch.menu.audio;
  // What the closed control prints for `auto`: the original's own row label, "Original"/"OG" only
  // when the manifest gave it neither a label nor a language.
  const originalLanguage = original === undefined ? null : languageOrNull(original);
  const autoValue = original === undefined ? words.original : audioRowLabel(tracks, original, true);
  const autoShort = originalLanguage === null ? words.originalShort : shortLanguageCode(originalLanguage);
  return {
    id: 'audio',
    label: words.label,
    value:
      selectedCode === null || active === undefined
        ? autoValue
        : audioRowLabel(tracks, active, active.key === originalKey),
    // The bar prints a code where the accessible name speaks the language: "DE" — or "OG" for an
    // original with no language tag, whatever language that turns out to be.
    valueShort: selectedCode === null ? autoShort : shortLanguageCode(selectedCode),
    sections: [
      { title: null, items },
      // The settings toggle, repeated where the choice is made — the quality menu's codec section
      // is the same shape. A checkbox among radios, because it composes with the language above
      // rather than competing with it.
      {
        title: null,
        items: [
          {
            id: STABLE_VOLUME_ID,
            label: words.stableVolume,
            detail: null,
            selected: stable,
            checkbox: true,
          },
        ],
      },
    ],
    note: null,
  };
}

/** A track's language as a display value, or `null` for a tag that says nothing (`""`, `und`). */
function languageOrNull(track: AudioTrackInfo): string | null {
  const language = track.language.trim();
  return language === '' || language.toLowerCase() === 'und' ? null : language;
}

/**
 * A row's left label: YouTube's own label where the manifest has one, with three edits. A
 * terminal "(XX)" that only repeats the language code comes off — "German (DE)" says German twice,
 * while "English (US)" is an accent worth keeping — the original's row trades the label's own
 * trailing "original" for the stated suffix, so it reads like every other language row instead of a
 * differently-shaped first entry, and a label that is *already* the word "Original" keeps it and
 * takes no suffix at all: a single-language manifest labels its original exactly that, and the
 * row read "Original - Original".
 */
function audioRowLabel(
  tracks: readonly AudioTrackInfo[],
  track: AudioTrackInfo,
  isOriginal: boolean,
): string {
  const words = text().watch.menu.audio;
  const own = strippedAudioLabel(track, isOriginal);
  // Nothing left of this rendition's own label: borrow the other rendition of the same language.
  const named = own === '' ? (sameLanguageLabel(tracks, track, isOriginal) ?? '') : own;
  const base = named === '' ? languageOrNull(track) : named;
  if (isOriginal) {
    return base === null || isOriginalWord(base) ? words.original : words.originalEntry(base);
  }
  return base ?? track.language;
}

/**
 * Whether a label already *is* the fact `originalEntry` would append. The English literal is the test
 * that matters — the label is YouTube's own string and arrives in English whatever the app's locale —
 * with the catalogue's word checked too, for a locale whose copy happens to match one.
 */
function isOriginalWord(label: string): boolean {
  const trimmed = label.trim().toLowerCase();
  return trimmed === 'original' || trimmed === text().watch.menu.audio.original.trim().toLowerCase();
}

/**
 * The label with the stable-volume decoration removed, in **both** shapes the manifests use: a
 * terminal "(Stable Volume)" on a multi-language video, and the entire label on a single-language one
 * (measured 2026-08-02 — `kC5oO7tzphg` labels its pair `Original` and `Stable Volume`). With the
 * switch on it would otherwise decorate every row for a fact the switch itself states.
 */
function strippedAudioLabel(track: AudioTrackInfo, isOriginal: boolean): string {
  const raw = (track.label?.trim() ?? '')
    .replace(/\s*\(stable volume\)$/i, '')
    .replace(/^stable volume$/i, '');
  return stripRedundantCountry(
    isOriginal ? raw.replace(/\s+original$/i, '') : raw,
    track.language,
  );
}

/**
 * The label of another rendition of the same language. A language's name is a fact about the
 * language, not about its dynamics, so the row must read the same whether the switch is on or off —
 * and on a single-language manifest the DRC track's whole label is the decoration, leaving nothing.
 */
function sameLanguageLabel(
  tracks: readonly AudioTrackInfo[],
  track: AudioTrackInfo,
  isOriginal: boolean,
): string | null {
  const language = track.language.toLowerCase();
  for (const candidate of tracks) {
    if (candidate === track || candidate.language.toLowerCase() !== language) continue;
    const label = strippedAudioLabel(candidate, isOriginal);
    if (label !== '') return label;
  }
  return null;
}

/** The terminal "(XX)" bracket, removed when it merely repeats the code's own language subtag. */
function stripRedundantCountry(label: string, languageCode: string): string {
  const primary = (languageCode.split('-')[0] ?? languageCode).trim().toLowerCase();
  const match = /^(.*?)\s*\(([A-Za-z]{2,3})\)$/.exec(label);
  const name = match?.[1] ?? '';
  const bracket = match?.[2] ?? '';
  return name !== '' && bracket.toLowerCase() === primary ? name : label;
}

export function withAudioSelection(
  preferences: PlaybackPreferences,
  itemId: string,
): PlaybackPreferences {
  // The rendition survives a language change: it is the switch's fact, not the row's.
  const drc = preferences.audio.drc === true ? { drc: true as const } : {};
  // The original's row: stored as `auto` — "the original" is a fact about every video where
  // a language code is only a fact about this one.
  if (itemId === AUTO_ID) return { ...preferences, audio: { kind: 'auto', ...drc } };
  // `allowDub: true` — the user named a language, and on this corpus every language but one *is* a
  // dub. Filtering them out here would make most of the menu's own entries unselectable.
  return { ...preferences, audio: { kind: 'lang', code: itemId, allowDub: true, ...drc } };
}

/** The audio menu's stable-volume switch — not a language row, so not a language id. */
export const STABLE_VOLUME_ID = 'stable-volume';

/**
 * Flip the rendition and keep the language: the switch composes with the row above it. The caller
 * persists the same value as `useStableVolume` — the menu's switch *is* the settings toggle.
 */
export function withStableVolume(preferences: PlaybackPreferences, on: boolean): PlaybackPreferences {
  const audio = preferences.audio;
  const drc = on ? { drc: true as const } : {};
  return {
    ...preferences,
    audio:
      audio.kind === 'auto'
        ? { kind: 'auto', ...drc }
        : { kind: 'lang', code: audio.code, allowDub: audio.allowDub, ...drc },
  };
}

/**
 * The code a tight trigger prints for a language: the primary subtag, uppercased — `de` and `de-DE`
 * are both "DE". Codes come from the MPD's `lang` and the captions list, so both casings and both
 * shapes occur; the region would double the width for a distinction the menu itself states anyway.
 */
function shortLanguageCode(code: string): string {
  return (code.split('-')[0] ?? code).toUpperCase();
}

/**
 * Whether a menu option is the DRC variant. Read from the role rather than the label: the label is
 * YouTube's display string ("English original (Stable Volume)") and the role is what
 * `playback/select.ts` decides on, so matching on the label would put the two out of step.
 */
function isDrcOption(track: AudioTrackInfo): boolean {
  return track.roles.some((role) => role.toLowerCase() === 'enhanced-audio-intelligibility');
}

/**
 * §7's subtitle row, built from the plan rather than from the engine: captions are sideloaded, so
 * they exist before `load()` and the menu does not have to wait for a manifest.
 *
 * One entry per language code, not per track. `en` appears **twice** on a video with auto-generated
 * captions (§2) and `SubtitlePref` addresses a track by *language*, so offering both would give two
 * rows that resolve to the same track — `decideSubtitle` prefers the human-authored one. Showing the
 * one that can actually be selected is more honest than showing a choice that silently collapses.
 */
export function subtitleMenu(
  tracks: readonly SubtitleTrackRef[],
  preferences: PlaybackPreferences,
): PlayerMenu | null {
  // Human-authored tracks are considered first, so they are the ones that survive the dedupe — the
  // same order of preference `decideSubtitle` applies, kept in step with it deliberately. A stable
  // sort keeps the API's own language ordering intact within each half.
  const ranked = [...subtitleOptions(tracks)].sort(
    (a, b) => Number(a.autoGenerated) - Number(b.autoGenerated),
  );
  const seen = new Set<string>();
  const options: SubtitleTrackRef[] = [];
  for (const track of ranked) {
    const code = track.languageCode.toLowerCase();
    if (seen.has(code)) continue;
    seen.add(code);
    options.push(track);
  }
  if (options.length === 0) return null;

  const pref = preferences.subtitle;
  const selectedCode = pref.kind === 'lang' ? pref.code.toLowerCase() : null;
  const selected = options.find((track) => track.languageCode.toLowerCase() === selectedCode);

  const words = text().watch.menu.subtitles;
  return {
    id: 'subtitles',
    label: words.label,
    value: selected === undefined ? words.off : selected.label,
    // Off prints nothing — the icon alone is the control; a chosen track prints its code.
    valueShort: selected === undefined ? null : shortLanguageCode(selected.languageCode),
    sections: [
      {
        title: null,
        items: [
          { id: OFF_ID, label: words.off, detail: null, selected: pref.kind === 'off' },
          ...options.map((track) => ({
            id: track.languageCode,
            label: track.label,
            detail: track.autoGenerated ? words.autoGenerated : null,
            selected: track.languageCode.toLowerCase() === selectedCode,
          })),
        ],
      },
    ],
    note: null,
  };
}

export function withSubtitleSelection(
  preferences: PlaybackPreferences,
  itemId: string,
): PlaybackPreferences {
  if (itemId === OFF_ID) return { ...preferences, subtitle: { kind: 'off' } };
  return {
    ...preferences,
    subtitle: { kind: 'lang', code: itemId, allowAutoGenerated: true },
  };
}

/** `C` — off ↔ the language already chosen, or the first one offered if none has been. */
export function withCaptionsToggled(
  preferences: PlaybackPreferences,
  tracks: readonly SubtitleTrackRef[],
  /** The last language turned on in this session, so `C` restores a choice rather than resetting it. */
  lastCode: string | null,
): PlaybackPreferences {
  if (preferences.subtitle.kind === 'lang') return { ...preferences, subtitle: { kind: 'off' } };

  const code = lastCode ?? subtitleOptions(tracks)[0]?.languageCode ?? null;
  if (code === null) return preferences;
  return { ...preferences, subtitle: { kind: 'lang', code, allowAutoGenerated: true } };
}

export function speedMenu(preferences: PlaybackPreferences): PlayerMenu {
  const words = text().watch.menu.speed;
  const rate = clampPlaybackRate(preferences.playbackRate);
  return {
    id: 'speed',
    label: words.label,
    value: words.rate(rate),
    sections: [
      {
        title: null,
        items: PLAYBACK_RATES.map((candidate) => ({
          id: String(candidate),
          label: candidate === 1 ? words.normal : words.rate(candidate),
          detail: null,
          selected: candidate === rate,
        })),
      },
    ],
    note: null,
  };
}

export function withRateSelection(
  preferences: PlaybackPreferences,
  itemId: string,
): PlaybackPreferences {
  return { ...preferences, playbackRate: clampPlaybackRate(Number(itemId)) };
}

/**
 * How far `,`/`.` move. The frame rate is per-track and `null` on plenty of them, so 30 fps is the
 * fallback — one frame at the wrong rate still steps by roughly a frame, whereas refusing to step
 * because the manifest omitted a number would disable the control on half the corpus.
 */
export function frameStepSeconds(tracks: readonly VideoTrackInfo[]): number {
  const active = tracks.find((track) => track.active) ?? tracks[0];
  const fps = active?.frameRate;
  return 1 / (fps !== undefined && fps !== null && fps > 0 ? fps : 30);
}
